/** /trip-types/:id/:tab? — trip-type ("Filter") editor shell. Tabs: General +
 *  Used by. */
import DeleteIcon from '@mui/icons-material/Delete';
import { Alert, Box, Button, CircularProgress, Stack, Tab, Tabs, Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState, type ReactNode } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';

import { ConsoleApiError } from '@/api/client';
import * as tripTypesApi from '@/api/tripTypes';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { HistoryPanel } from '@/components/History/HistoryPanel';
import { PageHeader } from '@/components/PageHeader';
import { IdentitySubtitle } from '@/components/ValueActions/identity';
import { DetailSubtitle, ValueActions } from '@/components/ValueActions/ValueActions';
import { tripTypesHref } from '@/utils/gridLinks';
import { PermissionGate } from '@/components/PermissionGate';
import { useSnackbar } from '@/components/Snackbar';
import { UsedByPanel } from '@/components/UsedByPanel/UsedByPanel';
import { usePermissions } from '@/hooks/usePermissions';
import { formatRelativeTime } from '@/utils/format';

import { copyActions, webUrlActions } from '../catalog/catalogLinks';

import { TripTypeGeneralTab } from './tabs/TripTypeGeneralTab';

const TABS = [
  { id: 'general', label: 'General' },
  { id: 'used-by', label: 'Used by' },
] as const;

export function TripTypeDetailPage() {
  const { id: idStr, tab } = useParams<{ id: string; tab?: string }>();
  const id = Number(idStr);
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const qc = useQueryClient();
  const { hasPerm } = usePermissions();
  const [confirmDelete, setConfirmDelete] = useState(false);

  const {
    data: tripType,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['trip_types', 'detail', id],
    queryFn: () => tripTypesApi.retrieve(id),
    enabled: Number.isFinite(id),
  });
  const onSaved = () => void refetch();

  const deleteMutation = useMutation({
    mutationFn: () => tripTypesApi.remove(id),
    onSuccess: () => {
      snackbar.show('Trip type deleted.', 'success');
      qc.invalidateQueries({ queryKey: ['trip_types'] });
      navigate('/trip-types');
    },
    onError: (e) =>
      snackbar.show(e instanceof ConsoleApiError ? e.message : 'Delete failed.', 'error'),
  });

  const canViewHistory = hasPerm('console_audit_view');
  const tabs = useMemo(
    () => (canViewHistory ? [...TABS, { id: 'history', label: 'History' } as const] : [...TABS]),
    [canViewHistory],
  );
  const activeTab = useMemo(() => tabs.find((t) => t.id === tab)?.id ?? 'general', [tabs, tab]);

  if (!Number.isFinite(id)) return <Alert severity="warning">Invalid trip type id.</Alert>;
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (error || !tripType) return <Alert severity="error">Couldn’t load trip type.</Alert>;

  return (
    <Box>
      <PageHeader
        helpStory="regions-trip-types"
        section={tabs.find((t) => t.id === activeTab)?.label}
        title={tripType.name}
        subtitle={
          <IdentitySubtitle
            tenant={tripType.tenant}
            grid={
              tripType.tenant
                ? { href: tripTypesHref({ tenant: tripType.tenant.id }), label: 'Trip types of this operator' }
                : null
            }
            id={tripType.id}
          />
        }
        actions={
          <Stack direction="row" spacing={1} alignItems="center">
            <PermissionGate perm="console_trip_types_manage">
              <Button
                size="small"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => setConfirmDelete(true)}
              >
                Delete
              </Button>
            </PermissionGate>
          </Stack>
        }
      />

      {/* Two payload fields the page never showed: the external id (trip types
          are not in the ext-ids `model` vocabulary, so it only copies) and the
          Branch.io deep link that opens this filter in the app. */}
      {(tripType.ext_id || tripType.branchio_link) && (
        <Typography variant="body2" color="text.secondary" component="div" sx={{ mt: -1.5, mb: 2 }}>
          <DetailSubtitle
            parts={[
              tripType.ext_id && (
                <ValueActions key="ext" actions={copyActions(tripType.ext_id, 'Copy external id')}>
                  Ext ID {tripType.ext_id}
                </ValueActions>
              ),
              tripType.branchio_link && (
                <ValueActions key="app" actions={webUrlActions(tripType.branchio_link, 'Open app link')}>
                  App link
                </ValueActions>
              ),
            ]}
          />
        </Typography>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={(_, v) => navigate(`/trip-types/${id}/${v}`)}>
          {tabs.map((t) => (
            <Tab key={t.id} value={t.id} label={t.label} />
          ))}
        </Tabs>
      </Box>

      <TabPane show={activeTab === 'general'}>
        <TripTypeGeneralTab tripType={tripType} onSaved={onSaved} />
      </TabPane>
      <TabPane show={activeTab === 'used-by'}>
        <UsedByPanel resource="trip_types" id={tripType.id} />
      </TabPane>
      <TabPane show={activeTab === 'history'}>
        <HistoryPanel contentType="demo.trip_type" objectPk={String(tripType.id)} onRestored={onSaved} />
      </TabPane>

      <Box sx={{ mt: 4, color: 'text.secondary', fontSize: 13 }}>
        Last updated {formatRelativeTime(tripType.updated_ts)} ·{' '}
        <RouterLink to={tripTypesHref({ tenant: tripType.tenant?.id })} style={{ color: '#0a1a3f' }}>
          ← Back to trip types
        </RouterLink>
      </Box>

      <ConfirmDialog
        open={confirmDelete}
        title="Delete this trip type?"
        message={`"${tripType.name}" will be deleted. This cannot be undone.`}
        confirmLabel="Delete trip type"
        destructive
        onConfirm={() => {
          setConfirmDelete(false);
          deleteMutation.mutate();
        }}
        onCancel={() => setConfirmDelete(false)}
      />
    </Box>
  );
}

function TabPane({ show, children }: { show: boolean; children: ReactNode }) {
  if (!show) return null;
  return <Box>{children}</Box>;
}
